import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useEffect} from "react";
import {useForm} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Textarea} from "@/components/ui/textarea";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import useRevokeHouseholdOccupant from "@/src/modules/households/hooks/use-revoke-household-occupant";
import {
  revokeHouseholdOccupantSchema,
  type RevokeHouseholdOccupantInput,
} from "@/src/modules/households/schemas/revoke-household-occupant";

type RevokeTenantSheetProps = {
  open: boolean;
  householdId: number | null;
  contextPropertyId: number | null;
  onClose: () => void;
};

export function RevokeTenantSheet({
  open,
  householdId,
  contextPropertyId,
  onClose,
}: RevokeTenantSheetProps) {
  const {mutateAsync: revokeOccupant, isPending} = useRevokeHouseholdOccupant({
    onSuccess: onClose,
  });

  const form = useForm<RevokeHouseholdOccupantInput>({
    resolver: zodResolver(revokeHouseholdOccupantSchema),
    defaultValues: {reason: ""},
  });
  const {watch} = form;
  const reason = watch("reason");
  const hasAllRequiredFields = reason.trim().length >= 10;

  useEffect(() => {
    if (!open) {
      form.reset({reason: ""});
    }
  }, [open, form]);

  if (!householdId || contextPropertyId == null) return null;

  const handleSubmit = form.handleSubmit(async (values) => {
    await revokeOccupant({
      id: householdId,
      body: {
        reason: values.reason,
        context_property_id: contextPropertyId,
      },
    });
  });

  return (
    <Sheet
      open={open}
      askConfirmation={() => Object.keys(form.formState.dirtyFields).length > 0}
      preventClose={isPending}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <div className="shrink-0">
          <SheetHeader className="px-4 pt-4 pb-3">
            <SheetTitle>End tenancy</SheetTitle>
          </SheetHeader>
          <div className="border-b border-border/60" />
          <p className="text-muted-foreground px-4 pt-3 pb-3 text-sm">
            The tenant will lose access to this unit. You will remain the owner and can invite a new
            tenant later.
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={handleSubmit} className={FORM_SHEET_ROOT_FORM_CLASS}>
            <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
              <FormField
                control={form.control}
                name="reason"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Reason</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="e.g. Lease ended and tenant moved out"
                        disabled={isPending}
                      />
                    </FormControl>
                    <FormDescription>At least 10 characters.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SheetFormScrollBody>

            <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
              <Button
                type="submit"
                variant="destructive"
                className="w-full"
                disabled={isPending || !hasAllRequiredFields}
              >
                End tenancy
                {isPending ? <Loader2 className="ml-2 size-4 animate-spin" /> : null}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
